import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ImagePlus, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";

const API = getApiUrl();

const MODEL_LABELS = {
  ideogram_4: "Ideogram 4",
  gpt_image_2: "GPT Image 2",
  nano_banana_pro: "Nano Banana Pro",
  flux_2: "FLUX.2",
};

/**
 * Admin -> Integrations -> Icon Generation
 * Powers the AI Image mode of the Icon Generator tool. Uses the shared
 * Emergent Universal Key (fal.ai) — nothing for the admin to type in,
 * just status + a live test call, plus the shared error-logging switch.
 */
export default function IconGenerationIntegrationSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/integrations/icon-generation-status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      console.error("Failed to load Icon Generation status:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/icon-generation-test-call`, {}, { withCredentials: true });
      setResult({ ok: true, model: data.model });
      toast.success(`Test generation (${MODEL_LABELS[data.model] || data.model}) succeeded`);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Test call failed" });
      toast.error(e?.response?.data?.detail || "Test call failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="icon-generation-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="icon-generation-integration-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ImagePlus className="h-4 w-4" />
            Icon Generation
          </CardTitle>
          <CardDescription>
            Powers the "AI Image" mode of the Icon Generator (System &gt; Tools &gt; Icon Generator).
            Uses the shared Emergent Universal Key via fal.ai — no API key to configure here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm" data-testid="icon-generation-status">
            {status?.configured ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium">Connected</span>
                <span className="text-muted-foreground">— Universal Key configured</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="font-medium text-destructive">Emergent LLM Key not configured</span>
              </>
            )}
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>Models: {(status?.models || []).map((m) => MODEL_LABELS[m] || m).join(" · ")}</p>
            <p>Used by: {status?.used_by?.join(" · ")}</p>
          </div>

          <div className="text-xs rounded-md p-3 border border-border bg-muted/30" data-testid="icon-generation-credits-help">
            <p className="font-medium text-foreground">Need more credits?</p>
            <p className="text-muted-foreground mt-0.5">
              Each generation spends Universal Key balance. Go to <span className="font-medium text-foreground">Profile → Manage Plan → Universal Key → Add Balance</span> to top up, or turn on auto top-up so you never run out mid-task.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleTest}
              disabled={testing || !status?.configured}
              size="sm"
              data-testid="icon-generation-test-call-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Test Generation
            </Button>
          </div>

          {result && (
            <div
              className={`text-sm rounded-md p-3 border ${result.ok ? "border-green-200 bg-green-50 text-green-800" : "border-destructive/30 bg-destructive/5 text-destructive"}`}
              data-testid="icon-generation-test-result"
            >
              {result.ok ? (
                <p className="font-medium">Success — {MODEL_LABELS[result.model] || result.model} generated a test image</p>
              ) : (
                <>
                  <p>{result.error}</p>
                  {/credit/i.test(result.error) && (
                    <p className="mt-1 text-xs">Top up at Profile → Manage Plan → Universal Key → Add Balance.</p>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationApiLogToggle provider="icon_generation" />
    </div>
  );
}
